// src/antdviews/component/LogsMgmtContent.tsx

import React from 'react'
import {
    Layout,
    Table,
    Select,
    Button,
    Modal,
    Descriptions,
    Typography,
    Space,
    Row,
    Col,
    Card,
    Tag,
    Pagination,
    Popconfirm,
    message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
    FileTextOutlined,
    ReloadOutlined,
    DeleteOutlined,
} from '@ant-design/icons'
import axios from 'axios' // 假设使用 axios 发起请求
import { FilePath, UserMgmtApi } from '../../services/Api'
import { Global } from '../../services/Global'
import {
    DEFAULT_RPP,
    getSourceName,
} from '../../services/Macro'
import { browserUtil } from '../../utils/BrowserUtil'
import { mathUtil } from '../../utils/MathUtil'

const { Content } = Layout
const { Title, Text } = Typography

export interface LogItem {
    uuid: string
    id: number | string
    date: string
    size: string
    updateTime: string
    source?: number | string
    username?: string // 新增：用户字段
    filePath: string
    fileName: string
    hideDate?: boolean
    rowspan?: number
}

export interface LogsData {
    list: LogItem[]
    currentPage?: number
    totalPages: number
    totalRecords?: number
    pages?: number[]
}

interface LogsMgmtContentProps {
    trans: (key: string) => string
    logs: LogsData
    changeLogList: (logsPerPage: number, logsCurrentPage: number) => void
}

interface LogsMgmtContentState {
    logsPerPage: number
    logsCurrentPage: number
    detailVisible: boolean
    detailIndex: number
}

export default class LogsMgmtContent extends React.Component<LogsMgmtContentProps, LogsMgmtContentState> {
    state: LogsMgmtContentState = {
        logsPerPage: 10,
        logsCurrentPage: 1,
        detailVisible: false,
        detailIndex: -1,
    }

    componentWillMount() {
        Global.isMobile = browserUtil.checkMobile()
    }

    /** LPP: Logs Per Page */
    onLppChange = (value: number) => {
        const oldLogsPerPage = this.state.logsPerPage
        this.setState({ logsPerPage: value }, () => {
            this.setState(
                {
                    logsCurrentPage: mathUtil.getNewPage(
                        oldLogsPerPage,
                        this.state.logsCurrentPage,
                        this.state.logsPerPage
                    ),
                },
                () => {
                    this.updateLogList()
                }
            )
        })
    }

    // 利用表格页脚的页码组件浏览日志
    updateLogList = () => {
        this.props.changeLogList(this.state.logsPerPage, this.state.logsCurrentPage)
    }

    prevLogPage = () => {
        if (this.state.logsCurrentPage > 1) {
            this.setState({ logsCurrentPage: this.state.logsCurrentPage - 1 }, () => {
                this.updateLogList()
            })
        }
    }

    nextLogPage = () => {
        if (this.state.logsCurrentPage < this.props.logs.totalPages) {
            this.setState({ logsCurrentPage: this.state.logsCurrentPage + 1 }, () => {
                this.updateLogList()
            })
        }
    }

    firstLogPage = () => {
        if (this.state.logsCurrentPage > 1) {
            this.setState({ logsCurrentPage: 1 }, () => {
                this.updateLogList()
            })
        }
    }

    lastLogPage = () => {
        if (this.state.logsCurrentPage < this.props.logs.totalPages) {
            this.setState({ logsCurrentPage: this.props.logs.totalPages }, () => {
                this.updateLogList()
            })
        }
    }

    onLogPageChange = (page: number) => {
        this.setState({ logsCurrentPage: page }, () => {
            this.updateLogList()
        })
    }

    /** 刷新当前页 */
    refresh = () => {
        this.updateLogList()
    }

    showLogDetail = (index: number) => {
        this.setState({ detailVisible: true, detailIndex: index })
    }

    closeLogDetail = () => {
        this.setState({ detailVisible: false, detailIndex: -1 })
    }

    /** 删除日志确认后的处理逻辑 */
    handleDelete = async (record: LogItem) => {
        const t = this.props.trans;
        try {
            // 批量删除接口：POST 传 ids 数组（此处为单条，包成数组）
            const response = await axios.post(UserMgmtApi.SUPER_DEL_LOGS, {
                ids: [Number(record.id)],
                token: localStorage.getItem('token'),
                role: localStorage.getItem('role'),
                lang: (localStorage.getItem('lang') || 'zh_CN').split('_')[0],
            });

            if (response.status === 200 || response.data?.code === 200) {
                message.success(t('deleteSuccess') || '删除成功');
                this.updateLogList(); // 刷新列表
            } else {
                message.error(response.data?.message || t('deleteFailed') || '删除失败');
            }
        } catch (error) {
            console.error('Delete Error:', error);
            message.error(t('deleteFailed') || '删除失败，请稍后重试');
        }
    }

    /** PC 端表格列 */
    getPcColumns = (): ColumnsType<LogItem> => {
        const t = this.props.trans
        return [
            {
                title: t('date'),
                dataIndex: 'date',
                key: 'date',
                width: 50,
                onCell: (record: LogItem) => {
                    if (record.hideDate) {
                        return { rowSpan: 0 }
                    }
                    return { rowSpan: record.rowspan || 1 }
                },
                render: (text: string) => (
                    <Space>
                        {/* <FileTextOutlined style={{ color: '#8c8c8c' }} /> */}
                        <Text>{text}</Text>
                    </Space>
                ),
            },
            {
                title: t('logId'),
                dataIndex: 'id',
                key: 'id',
                width: 40,
                render: (text: number | string) => <Text>{text}</Text>,
            },
            {
                title: t('size'),
                dataIndex: 'size',
                key: 'size',
                width: 40,
                render: (text: string) => <Text type="secondary">{text}</Text>,
            },
            {
                title: t('updateTime'),
                dataIndex: 'updateTime',
                key: 'updateTime',
                width: 80,
                render: (text: string) => <Text type="secondary">{text}</Text>,
            },
            {
                title: t('source'),
                dataIndex: 'source',
                key: 'source',
                width: 40,
                render: (source: number) => (
                    <Tag color="blue">{getSourceName(source)}</Tag>
                ),
            },
            // 新增：用户列
            {
                title: t('nickname') || '昵称',
                dataIndex: 'username',
                key: 'username',
                width: 60,
                ellipsis: true,
                render: (text: string) => <Text>{text || '-'}</Text>,
            },
            {
                title: t('download'),
                key: 'download',
                width: 180,
                ellipsis: true,
                render: (_: any, record: LogItem) => (
                    <a
                        href={`${FilePath.LOGS}${record.filePath}`}
                        download={record.fileName}
                        title={record.fileName}
                    >
                        {record.fileName}
                    </a>
                ),
            },
            // 新增：操作列
            {
                title: t('operation') || '操作',
                key: 'action',
                width: 60,
                // fixed: 'right', // 固定在右侧
                render: (_: any, record: LogItem) => (
                    <Popconfirm
                        title={t('confirmDelete') || '确认删除'}
                        description={t('confirmDeleteDesc') || '确定要物理删除这条日志吗？此操作不可恢复。'}
                        onConfirm={() => this.handleDelete(record)}
                        okText={t('confirm') || '确定'}
                        cancelText={t('cancel') || '取消'}
                        okButtonProps={{ danger: true }}
                    >
                        <Button type="link" danger icon={<DeleteOutlined />}>
                            {t('delete') || '删除'}
                        </Button>
                    </Popconfirm>
                ),
            },
        ]
    }

    /** 移动端表格列 */
    getMobileColumns = (): ColumnsType<LogItem> => {
        const t = this.props.trans
        return [
            {
                title: '',
                key: 'action',
                width: 40,
                render: (_: any, __: LogItem, index: number) => (
                    <Button
                        type="link"
                        size="small"
                        icon={<span style={{ fontSize: 14 }}>▾</span>}
                        onClick={() => this.showLogDetail(index)}
                    />
                ),
            },
            {
                title: t('date'),
                dataIndex: 'date',
                key: 'date',
            },
            {
                title: t('size'),
                dataIndex: 'size',
                key: 'size',
            },
            {
                title: t('updateTime'),
                dataIndex: 'updateTime',
                key: 'updateTime',
            },
        ]
    }

    /** 日志详情弹窗 */
    renderDetailModal = () => {
        const t = this.props.trans
        const { detailIndex, detailVisible } = this.state
        const item = detailIndex >= 0 ? this.props.logs.list[detailIndex] : null
        if (!item) return null

        return (
            <Modal
                title={t('logDetail') || 'Log Detail'}
                open={detailVisible}
                onCancel={this.closeLogDetail}
                footer={[
                    <Button key="close" onClick={this.closeLogDetail}>
                        {t('close') || 'Close'}
                    </Button>,
                ]}
                width={600}
            >
                <Descriptions column={1} bordered size="small">
                    <Descriptions.Item label={t('download')}>
                        <a href={`${FilePath.LOGS}${item.filePath}`} download={item.fileName}>
                            {item.fileName}
                        </a>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('logId')}>{item.id}</Descriptions.Item>
                </Descriptions>
            </Modal>
        )
    }

    render() {
        if (Global.isMobile) return this.renderMobile()
        else return this.renderPc()
    }

    renderMobile() {
        // ... (保持原样) ...
        const t = this.props.trans
        const { logsPerPage, logsCurrentPage } = this.state
        const logCurP = this.props.logs.currentPage ?? logsCurrentPage
        const logTotalP = this.props.logs.totalPages ?? 0
        const logTotalR = this.props.logs.totalRecords ?? 0

        return (
            <Content
                style={{
                    margin: '4px',
                    padding: '6px',
                    background: 'var(--content-bg)',
                    height: 'calc(100vh - 8px)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                }}
            >
                <Card
                    title={
                        <Space>
                            <FileTextOutlined style={{ color: '#1890ff' }} />
                            <Title level={4} style={{ margin: 0 }}>
                                {t('logsMgmt')}
                            </Title>
                            <Tag color="blue">{logTotalR} {t('records') || '条记录'}</Tag>
                        </Space>
                    }
                    extra={
                        <Button icon={<ReloadOutlined />} onClick={this.refresh}>
                            {t('refresh') || '刷新'}
                        </Button>
                    }
                    style={{
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        background: 'var(--card-bg)',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                    }}
                    bodyStyle={{
                        padding: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                    }}
                >
                    <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                        <Table<LogItem>
                            rowKey={(record, index) => `${record.id}-${index}`}
                            columns={this.getMobileColumns()}
                            dataSource={this.props.logs.list}
                            pagination={false}
                            size="small"
                            bordered
                        />
                    </div>

                    <Row
                        justify="space-between"
                        align="middle"
                        style={{
                            marginTop: 16,
                            flexShrink: 0,
                            paddingTop: 8,
                            borderTop: '1px solid #f0f0f0',
                            width: '100%',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Col>
                            <Text type="secondary">
                                {t('table.footer.totalRecords') || '共'} {logTotalR} {t('table.footer.records') || '条记录'}
                                ，{t('table.footer.currentPage') || '当前第'} {logCurP}/{logTotalP} {t('table.footer.page') || ''}
                            </Text>
                        </Col>
                        <Col>
                            <Space>
                                <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
                                    {t('table.footer.pageSize') || '每页'}
                                </span>
                                <Select
                                    value={logsPerPage}
                                    onChange={this.onLppChange}
                                    style={{ width: 80 }}
                                    options={DEFAULT_RPP.map((item: number) => ({ label: item, value: item }))}
                                />
                                <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
                                    {t('items') || '条'}
                                </span>
                                <Pagination
                                    current={logCurP}
                                    total={logTotalR}
                                    pageSize={logsPerPage}
                                    onChange={this.onLogPageChange}
                                    showSizeChanger={false}
                                    simple
                                    size="small"
                                />
                            </Space>
                        </Col>
                    </Row>
                </Card>

                {this.renderDetailModal()}
            </Content>
        )
    }

    renderPc() {
        const t = this.props.trans
        const { logsPerPage, logsCurrentPage } = this.state
        const logCurP = this.props.logs.currentPage ?? logsCurrentPage
        const logTotalP = this.props.logs.totalPages ?? 0
        const logTotalR = this.props.logs.totalRecords ?? 0

        return (
            <Content
                style={{
                    margin: '4px',
                    padding: '6px',
                    background: 'var(--content-bg)',
                    height: 'calc(100vh - 75px)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                }}
            >
                <Card
                    title={
                        <Space>
                            <FileTextOutlined style={{ color: '#1890ff' }} />
                            <Title level={4} style={{ margin: 0 }}>
                                {t('logsMgmt')}
                            </Title>
                            <Tag color="blue">{logTotalR} {t('records') || '条记录'}</Tag>
                        </Space>
                    }
                    extra={
                        <Button icon={<ReloadOutlined />} onClick={this.refresh}>
                            {t('refresh') || '刷新'}
                        </Button>
                    }
                    style={{
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        background: 'var(--card-bg)',
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                    }}
                    bodyStyle={{
                        padding: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        overflow: 'hidden',
                        boxSizing: 'border-box',
                    }}
                >
                    {/* 表格区域：使用 flex: 1 和 minHeight: 0，确保不会撑破外层 */}
                    <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                        <Table<LogItem>
                            rowKey={(record, index) => `${record.id}-${index}`}
                            columns={this.getPcColumns()}
                            dataSource={this.props.logs.list}
                            pagination={false}
                            bordered
                            size="small"
                            style={{ borderRadius: 6 }}
                            scroll={{
                                x: 1300, // 增加横向滚动范围，避免新增列挤压原有列
                                y: 'calc(100vh - 240px)',
                            }}
                            components={{
                                header: {
                                    cell: (props: any) => (
                                        <th {...props} style={{ ...props.style, borderColor: '#bbb', borderWidth: 1 }} />
                                    ),
                                },
                                body: {
                                    cell: (props: any) => (
                                        <td {...props} style={{ ...props.style, borderColor: '#bbb', borderWidth: 1 }} />
                                    ),
                                },
                            }}
                        />
                    </div>

                    {/* 分页区域 */}
                    <Row
                        justify="space-between"
                        align="middle"
                        style={{
                            marginTop: 2,
                            flexShrink: 0,
                            paddingTop: 2,
                            borderTop: '1px solid #f0f0f0',
                            width: '100%',
                            boxSizing: 'border-box',
                        }}
                    >
                        <Col>
                            <Text type="secondary">
                                {t('table.footer.totalRecords') || '共'} {logTotalR} {t('table.footer.records') || '条记录'}
                                ，{t('table.footer.currentPage') || '当前第'} {logCurP}/{logTotalP} {t('table.footer.page') || ''}
                            </Text>
                        </Col>
                        <Col>
                            <Space>
                                <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
                                    {t('table.footer.pageSize') || '每页'}
                                </span>
                                <Select
                                    value={logsPerPage}
                                    onChange={this.onLppChange}
                                    style={{ width: 80 }}
                                    options={DEFAULT_RPP.map((item: number) => ({ label: item, value: item }))}
                                />
                                <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
                                    {t('table.footer.items') || '条'}
                                </span>
                                <Pagination
                                    current={logCurP}
                                    total={logTotalR}
                                    pageSize={logsPerPage}
                                    onChange={this.onLogPageChange}
                                    showSizeChanger={false}
                                    showQuickJumper
                                    locale={{
                                        items_per_page: t('table.footer.itemsPerPage') || '条/页',
                                        jump_to: t('table.footer.jumpTo') || '跳至',
                                        jump_to_confirm: t('table.footer.confirm') || '确定',
                                        page: t('table.footer.page') || '',
                                        prev_page: t('table.footer.prevPage') || '上一页',
                                        next_page: t('table.footer.nextPage') || '下一页',
                                        prev_5: t('table.footer.prev5') || '向前 5 页',
                                        next_5: t('table.footer.next5') || '向后 5 页',
                                        prev_3: t('table.footer.prev3') || '向前 3 页',
                                        next_3: t('table.footer.next3') || '向后 3 页',
                                    }}
                                />
                            </Space>
                        </Col>
                    </Row>
                </Card>

                {this.renderDetailModal()}
            </Content>
        )
    }
}